/**
 * Decorative overlay for duplicate (competitive) game tables.
 *
 * Three styles matching real-world card table designs:
 *   - dup-felt:   felt texture + corner labels NORD/EST/SUD/OUEST + border rectangle
 *   - dup-neo:    neo gradient + gold N/E/S/O + suit symbols + ornamental border
 *   - dup-legacy: legacy-v2 texture + white N/E/S/O + semicircle-cutout border
 *
 * Labels and decorations are responsive (percentage-based CSS positioning).
 * Text is rendered as HTML (no SVG distortion); borders use CSS or inline SVG.
 */

import { memo } from 'react';
import type { DuplicateTableStyle } from '../types/app';

interface Props {
  style: DuplicateTableStyle;
}

// ============================================
// Design 1 : Felt / Compétition style
// ============================================

function FeltDecor() {
  return (
    <>
      <div className="dup-rect dup-felt-rect" />
      <span className="dup-lbl dup-felt-lbl dup-felt-nord">NORD</span>
      <span className="dup-lbl dup-felt-lbl dup-felt-est">EST</span>
      <span className="dup-lbl dup-felt-lbl dup-felt-sud">SUD</span>
      <span className="dup-lbl dup-felt-lbl dup-felt-ouest">OUEST</span>
    </>
  );
}

// ============================================
// Design 2 : Neo / Prestige gold
// ============================================

function NeoDecor() {
  return (
    <>
      <div className="dup-rect dup-neo-rect" />
      <div className="dup-neo-corner dup-neo-corner-nw" />
      <div className="dup-neo-corner dup-neo-corner-ne" />
      <div className="dup-neo-corner dup-neo-corner-sw" />
      <div className="dup-neo-corner dup-neo-corner-se" />
      <span className="dup-lbl dup-neo-lbl dup-neo-n">N</span>
      <span className="dup-lbl dup-neo-lbl dup-neo-e">E</span>
      <span className="dup-lbl dup-neo-lbl dup-neo-s">S</span>
      <span className="dup-lbl dup-neo-lbl dup-neo-o">O</span>
      <span className="dup-suit dup-suit-nw">♦</span>
      <span className="dup-suit dup-suit-ne">♣</span>
      <span className="dup-suit dup-suit-sw">♠</span>
      <span className="dup-suit dup-suit-se">♥</span>
    </>
  );
}

// ============================================
// Design 3 : Legacy / White with semicircles
// ============================================

/**
 * SVG border path with semicircle cutouts around each N/E/S/O label.
 * Uses viewBox coordinates; stretched to container via preserveAspectRatio="none"
 * with non-scaling-stroke to keep consistent line width.
 */
function LegacyBorderPath() {
  const M = 60;
  const W = 1000;
  const R = 22;

  // Midpoints of each edge (in viewBox coords)
  const topMid = W / 2;
  const rightMid = W / 2;
  const botMid = W / 2;
  const leftMid = W / 2;

  // Path: start top-left, go clockwise, with semicircle arcs at midpoints
  const d = [
    `M ${M} ${M}`,
    // Top edge -> semicircle at top center
    `L ${topMid - R} ${M}`,
    `A ${R} ${R} 0 0 0 ${topMid + R} ${M}`,
    `L ${W - M} ${M}`,
    // Right edge -> semicircle at right center
    `L ${W - M} ${rightMid - R}`,
    `A ${R} ${R} 0 0 0 ${W - M} ${rightMid + R}`,
    `L ${W - M} ${W - M}`,
    // Bottom edge -> semicircle at bottom center
    `L ${botMid + R} ${W - M}`,
    `A ${R} ${R} 0 0 0 ${botMid - R} ${W - M}`,
    `L ${M} ${W - M}`,
    // Left edge -> semicircle at left center
    `L ${M} ${leftMid + R}`,
    `A ${R} ${R} 0 0 0 ${M} ${leftMid - R}`,
    `Z`,
  ].join(' ');

  return (
    <svg
      className="dup-legacy-svg"
      viewBox={`0 0 ${W} ${W}`}
      preserveAspectRatio="none"
    >
      <path
        d={d}
        fill="none"
        stroke="rgba(255,255,255,0.45)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function LegacyDecor() {
  return (
    <>
      <LegacyBorderPath />
      <span className="dup-lbl dup-legacy-lbl dup-legacy-n">N</span>
      <span className="dup-lbl dup-legacy-lbl dup-legacy-e">E</span>
      <span className="dup-lbl dup-legacy-lbl dup-legacy-s">S</span>
      <span className="dup-lbl dup-legacy-lbl dup-legacy-o">O</span>
    </>
  );
}

// ============================================
// Main component
// ============================================

const DuplicateTableDecor = memo(function DuplicateTableDecor({ style }: Props) {
  return (
    <div className={`dup-decor dup-decor-${style}`} aria-hidden>
      {style === 'dup-felt' && <FeltDecor />}
      {style === 'dup-neo' && <NeoDecor />}
      {style === 'dup-legacy' && <LegacyDecor />}
    </div>
  );
});

export default DuplicateTableDecor;
